/**
 * #621 (Chris, 2 Oct): a sign-out whose stamp the API did not take within 30 s still signs this browser out, and
 * the stamp is finished at that person's next sign-in here. This keeps the attempt's time per person: the retry
 * must carry it, because a stamp of "now" would be later than the new session and sign it straight out again.
 * localStorage, so it survives the sign-out's own redirect; a browser that refuses storage keeps nothing.
 */
const KEY = "starsach-signout-pending";

function read(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function write(all: Record<string, string>): void {
  try {
    if (Object.keys(all).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Storage refused (a private window): nothing to retry later, and nothing else to do.
  }
}

/** Keeps the earliest unrecorded sign-out for this person: the earliest covers every token a later one would. */
export function rememberPendingSignOut(subject: string, at: string): void {
  const all = read();
  const earlier = all[subject];
  all[subject] = earlier && earlier < at ? earlier : at;
  write(all);
}

/** A refresh token lives eight hours: a stamp older than that has nothing left to refuse (gairm/lms#439). */
const PENDING_FOR_MS = 8 * 3_600_000;

export function pendingSignOut(subject: string, now = Date.now()): string | undefined {
  const at = read()[subject];
  if (at && now - Date.parse(at) > PENDING_FOR_MS) {
    clearPendingSignOut(subject);
    return undefined;
  }
  return at;
}

export function clearPendingSignOut(subject: string): void {
  const all = read();
  delete all[subject];
  write(all);
}

/** Whether anyone's sign-out in this browser is still unrecorded: the sign-in page says so. */
export function hasPendingSignOut(): boolean {
  return Object.keys(read()).length > 0;
}
